import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Spin,
  Upload,
  message as toast,
} from 'antd';
import { CloseOutlined, PictureOutlined, VideoCameraOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import { PersonAvatar } from './shared/components';
import { FeedMediaView, FeedPostView } from './shared/types';
import { errorCode, feedError, makeClientRef, readVideoDuration, uploadFeedMedia } from './shared/utils';
import styles from './styles.module.scss';

/**
 * "Đăng bài HP Media" (03 §14.8) — dùng chính API của app (M1–M4, P5 với asPage:true), cùng cookie
 * phiên. Chỉ hiện với ADMIN và Biên tập trang (access.canPostAsPage).
 */

type ItemStatus = 'uploading' | 'processing' | 'ready' | 'failed' | 'error';

interface MediaItem {
  key: string;
  kind: 'IMAGE' | 'VIDEO';
  file: File;
  localUrl: string | null;
  status: ItemStatus;
  progress: number;
  mediaId?: number;
  message?: string;
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const POLL_STEPS = [2000, 5000, 10000];
const POLL_MAX_MS = 15 * 60 * 1000;
const MB = 1024 * 1024;

let keySeq = 0;
const nextKey = () => `m${Date.now().toString(36)}${(keySeq += 1)}`;

const TermsStep = ({ onAccepted }: { onAccepted: () => void }) => {
  const { access, reloadAccess } = useFeed();
  const [text, setText] = useState<string | null>(access.terms?.text ?? null);
  const [version, setVersion] = useState<number>(access.terms?.version ?? 1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (text) return;
    AxiosUtils.get(API.feedTerms)
      .then((res: any) => {
        if (res?.data?.text) setText(res.data.text);
        if (res?.data?.version) setVersion(res.data.version);
      })
      .catch((err) => toast.error(feedError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accept = async () => {
    setSaving(true);
    try {
      await AxiosUtils.post(API.feedTermsAccept, { version });
      await reloadAccess();
      onAccepted();
    } catch (err) {
      if (errorCode(err) === 'FEED_TERMS_VERSION_MISMATCH') {
        toast.info('Quy tắc vừa được cập nhật, vui lòng đọc lại.');
        setText(null);
        await reloadAccess();
      } else {
        toast.error(feedError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Đồng ý Quy tắc đăng bài trước khi đăng bài đầu tiên (hoặc khi Quy tắc có phiên bản mới)."
      />
      <div className={styles.termsText}>{text || <Spin size="small" />}</div>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Button type="primary" loading={saving} disabled={!text} onClick={accept}>
          Tôi đồng ý
        </Button>
      </div>
    </div>
  );
};

const ComposerBody = ({ onClose }: { onClose: () => void }) => {
  const { access, afterAction, openPost, reloadAccess } = useFeed();
  const limits = access.limits;
  const clientRef = useMemo(() => makeClientRef(), []);
  const [content, setContent] = useState('');
  const [items, setItems] = useState<MediaItem[]>([]);
  const [allowComments, setAllowComments] = useState(true);
  const [important, setImportant] = useState(false);
  const [days, setDays] = useState<number>(limits?.importantDefaultDays || 7);
  const [notify, setNotify] = useState(false);
  const [posting, setPosting] = useState(false);
  const [termsDone, setTermsDone] = useState(false);
  const timers = useRef<Record<string, number>>({});
  const alive = useRef(true);
  const queue = useRef<MediaItem[]>([]);
  const activeUploads = useRef(0);

  useEffect(() => {
    // Đặt lại true mỗi lần gắn: StrictMode (dev) gắn → gỡ → gắn lại, nếu không thì alive kẹt ở
    // false và mọi patch() bị bỏ qua (ảnh/video đứng mãi ở "0%").
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      Object.values(pending).forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const patch = (key: string, next: Partial<MediaItem>) => {
    if (!alive.current) return;
    setItems((list) => list.map((it) => (it.key === key ? { ...it, ...next } : it)));
  };

  // ---------------------------------------------------------------- tải ảnh (tối đa 2 request song song)

  const pump = () => {
    while (activeUploads.current < 2 && queue.current.length) {
      const item = queue.current.shift() as MediaItem;
      activeUploads.current += 1;
      const form = new FormData();
      form.append('files', item.file);
      uploadFeedMedia(API.feedMediaImages, form, (pct) => patch(item.key, { progress: pct }))
        .then((res: any) => {
          const list: FeedMediaView[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          const media = list[0];
          if (!media) throw new Error('Máy chủ không trả ảnh.');
          patch(item.key, {
            mediaId: media.id,
            progress: 100,
            status: media.status === 'FAILED' ? 'failed' : 'ready',
            message: media.status === 'FAILED' ? media.failText || 'Ảnh lỗi' : undefined,
          });
        })
        .catch((err) => patch(item.key, { status: 'error', message: feedError(err) }))
        .finally(() => {
          activeUploads.current -= 1;
          pump();
        });
    }
  };

  const addImages = (files: File[]) => {
    const current = items.filter((i) => i.kind === 'IMAGE').length;
    const room = Math.max(0, (limits?.imageMaxCount || 10) - current);
    if (files.length > room) toast.warning(`Tối đa ${limits?.imageMaxCount || 10} ảnh mỗi bài.`);
    const accepted: MediaItem[] = [];
    files.slice(0, room).forEach((file) => {
      if (/\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type)) {
        toast.error('Ảnh HEIC chưa hỗ trợ, hãy chọn JPG/PNG.');
        return;
      }
      if (!IMAGE_TYPES.includes(file.type)) {
        toast.error(`“${file.name}”: chỉ chọn ảnh JPG, PNG, WEBP hoặc GIF.`);
        return;
      }
      if (file.size > (limits?.imageMaxMb || 10) * MB) {
        toast.error(`Ảnh “${file.name}” quá lớn (tối đa ${limits?.imageMaxMb} MB).`);
        return;
      }
      accepted.push({
        key: nextKey(),
        kind: 'IMAGE',
        file,
        localUrl: URL.createObjectURL(file),
        status: 'uploading',
        progress: 0,
      });
    });
    if (!accepted.length) return;
    setItems((list) => list.concat(accepted));
    queue.current = queue.current.concat(accepted);
    pump();
  };

  // ---------------------------------------------------------------- tải video + hỏi trạng thái (M2, M3)

  const poll = (key: string, mediaId: number, startedAt: number, step: number) => {
    const delay = POLL_STEPS[Math.min(step, POLL_STEPS.length - 1)];
    timers.current[key] = window.setTimeout(async () => {
      if (!alive.current) return;
      try {
        const res: any = await AxiosUtils.get(`${API.feedMedia}/${mediaId}`);
        const media = res?.data as FeedMediaView;
        if (media?.status === 'READY') {
          patch(key, { status: 'ready' });
          return;
        }
        if (media?.status === 'FAILED') {
          patch(key, { status: 'failed', message: media.failText || media.failReason || 'Video lỗi' });
          return;
        }
      } catch {
        // Lỗi mạng tạm thời: hỏi lại lần sau.
      }
      if (Date.now() - startedAt < POLL_MAX_MS) poll(key, mediaId, startedAt, step + 1);
    }, delay);
  };

  const addVideo = async (file: File) => {
    if (!VIDEO_TYPES.includes(file.type) && !/\.(mp4|mov|webm)$/i.test(file.name)) {
      toast.error('Chỉ chọn video MP4, MOV hoặc WEBM.');
      return;
    }
    const maxMb = limits?.videoMaxMb || 200;
    if (file.size > maxMb * MB) {
      toast.error(`Video quá lớn (tối đa ${maxMb} MB).`);
      return;
    }
    const duration = await readVideoDuration(file);
    const maxSeconds = limits?.videoMaxSeconds || 60;
    if (duration !== null && duration > maxSeconds + 1) {
      toast.error(`Video dài ${Math.round(duration)} giây, tối đa ${maxSeconds} giây.`);
      return;
    }
    const item: MediaItem = { key: nextKey(), kind: 'VIDEO', file, localUrl: null, status: 'uploading', progress: 0 };
    setItems((list) => list.concat(item));
    const form = new FormData();
    form.append('file', file);
    try {
      const res: any = await uploadFeedMedia(API.feedMediaVideo, form, (pct) => patch(item.key, { progress: pct }));
      const media = (res?.data && res.data.id ? res.data : res) as FeedMediaView;
      if (!media?.id) throw new Error('Máy chủ không trả video.');
      if (media.status === 'READY') {
        patch(item.key, { mediaId: media.id, progress: 100, status: 'ready' });
      } else if (media.status === 'FAILED') {
        patch(item.key, { mediaId: media.id, status: 'failed', message: media.failText || 'Video lỗi' });
      } else {
        patch(item.key, { mediaId: media.id, progress: 100, status: 'processing' });
        poll(item.key, media.id, Date.now(), 0);
      }
    } catch (err) {
      patch(item.key, { status: 'error', message: feedError(err) });
    }
  };

  const removeItem = (item: MediaItem) => {
    if (timers.current[item.key]) window.clearTimeout(timers.current[item.key]);
    queue.current = queue.current.filter((q) => q.key !== item.key);
    if (item.localUrl) URL.revokeObjectURL(item.localUrl);
    if (item.mediaId) {
      AxiosUtils._delete(`${API.feedMedia}/${item.mediaId}`, undefined).catch(() => undefined);
    }
    setItems((list) => list.filter((i) => i.key !== item.key));
  };

  // ---------------------------------------------------------------- đăng (P5)

  const hasVideo = items.some((i) => i.kind === 'VIDEO');
  const hasImages = items.some((i) => i.kind === 'IMAGE');
  const uploading = items.some((i) => i.status === 'uploading');
  const broken = items.some((i) => i.status === 'error' || i.status === 'failed');
  const charCount = Array.from(content).length;
  const maxLen = limits?.textMaxLen || 5000;
  const canPost = (content.trim() || items.length) && !uploading && !broken && charCount <= maxLen;

  const submit = async () => {
    if (!canPost) return;
    setPosting(true);
    try {
      const body: Record<string, unknown> = {
        content: content.trim() || undefined,
        mediaIds: items.map((i) => i.mediaId).filter((id): id is number => !!id),
        asPage: true,
        allowComments,
        clientRef,
      };
      if (important && access.canMarkImportant) body.important = { days, notify };
      const res: any = await AxiosUtils.post(API.feedPosts, body);
      const post = res?.data as FeedPostView;
      toast.success('Đã đăng bài tên HP Media.');
      items.forEach((i) => i.localUrl && URL.revokeObjectURL(i.localUrl));
      onClose();
      afterAction();
      if (post?.id) openPost(post.id);
    } catch (err) {
      if (errorCode(err) === 'FEED_TERMS_REQUIRED') {
        setTermsDone(false);
        await reloadAccess();
      }
      toast.error(feedError(err));
    } finally {
      setPosting(false);
    }
  };

  const discard = () => {
    const dirty = content.trim() || items.length;
    const doClose = () => {
      items.forEach((i) => removeItem(i));
      onClose();
    };
    if (!dirty) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Bỏ bài đang viết?',
      content: 'Chữ và ảnh/video đã tải lên sẽ bị bỏ.',
      okText: 'Bỏ bài',
      okButtonProps: { danger: true },
      cancelText: 'Viết tiếp',
      onOk: doClose,
    });
  };

  const needTerms = access.terms && !access.terms.accepted && !termsDone;
  const pageName = access.page?.name || 'HP Media';

  return (
    <Modal
      open
      width={640}
      title="Đăng bài tên HP Media"
      onCancel={discard}
      maskClosable={false}
      destroyOnHidden
      footer={
        needTerms
          ? null
          : [
              <Button key="cancel" onClick={discard}>
                Huỷ
              </Button>,
              <Button key="post" type="primary" loading={posting} disabled={!canPost} onClick={submit}>
                Đăng bài
              </Button>,
            ]
      }
    >
      {needTerms ? (
        <TermsStep onAccepted={() => setTermsDone(true)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Space>
            <PersonAvatar group name={pageName} avatar={access.page?.avatarUrl || null} size={40} />
            <strong>{pageName}</strong>
          </Space>
          <Input.TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Viết thông báo cho Quản lý, Agency, Creator, Nhân viên…"
            autoSize={{ minRows: 4, maxRows: 14 }}
          />
          <div style={{ textAlign: 'right', fontSize: 12, color: charCount > maxLen ? colors.dangerInk : colors.textSecondary }}>
            {charCount}/{maxLen}
          </div>
          <Space wrap>
            <Upload
              accept={IMAGE_TYPES.join(',')}
              multiple
              showUploadList={false}
              disabled={hasVideo}
              beforeUpload={(file, fileList) => {
                if (file === fileList[0]) addImages(fileList as unknown as File[]);
                return false;
              }}
            >
              <Button icon={<PictureOutlined />} disabled={hasVideo}>
                Thêm ảnh (tối đa {limits?.imageMaxCount ?? 10})
              </Button>
            </Upload>
            {limits?.videoEnabled && (
              <Upload
                accept={VIDEO_TYPES.join(',')}
                showUploadList={false}
                maxCount={1}
                disabled={hasImages || hasVideo}
                beforeUpload={(file) => {
                  addVideo(file as unknown as File);
                  return false;
                }}
              >
                <Button icon={<VideoCameraOutlined />} disabled={hasImages || hasVideo}>
                  Thêm video (≤ {limits.videoMaxSeconds} giây, ≤ {limits.videoMaxMb} MB)
                </Button>
              </Upload>
            )}
          </Space>
          {items.length > 0 && (
            <div className={styles.composerMedia}>
              {items.map((item) => (
                <div
                  key={item.key}
                  className={`${styles.composerChip} ${item.kind === 'VIDEO' ? styles.composerChipWide : ''}`}
                  style={
                    item.status === 'error' || item.status === 'failed'
                      ? { borderColor: colors.dangerInk, color: colors.dangerInk }
                      : undefined
                  }
                >
                  {item.kind === 'IMAGE' && item.localUrl && item.status !== 'error' ? (
                    <img src={item.localUrl} alt="" />
                  ) : null}
                  {item.kind === 'VIDEO' && (
                    <div style={{ width: '100%' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-all' }}>
                        <VideoCameraOutlined /> {item.file.name}
                      </div>
                      {item.status === 'uploading' && <Progress percent={item.progress} size="small" />}
                      {item.status === 'processing' && <span>Đang xử lý video… (đăng được ngay)</span>}
                      {item.status === 'ready' && <span style={{ color: colors.successInk }}>Sẵn sàng</span>}
                      {(item.status === 'failed' || item.status === 'error') && <span>{item.message}</span>}
                    </div>
                  )}
                  {item.kind === 'IMAGE' && item.status === 'uploading' && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,.7)',
                      }}
                    >
                      {item.progress}%
                    </div>
                  )}
                  {item.kind === 'IMAGE' && (item.status === 'error' || item.status === 'failed') && (
                    <span style={{ padding: 4 }}>{item.message || 'Lỗi'}</span>
                  )}
                  <Button
                    className={styles.chipRemove}
                    size="small"
                    shape="circle"
                    icon={<CloseOutlined />}
                    aria-label="Bỏ tệp này"
                    onClick={() => removeItem(item)}
                  />
                </div>
              ))}
            </div>
          )}
          {broken && (
            <div style={{ fontSize: 12, color: colors.dangerInk }}>Bỏ các tệp lỗi (✕) trước khi đăng.</div>
          )}
          <Checkbox checked={allowComments} onChange={(e) => setAllowComments(e.target.checked)}>
            Cho phép bình luận
          </Checkbox>
          {access.canMarkImportant && (
            <Space wrap>
              <Checkbox checked={important} onChange={(e) => setImportant(e.target.checked)}>
                Đánh dấu Quan trọng
              </Checkbox>
              {important && (
                <>
                  <span>Hiện ở đầu Bảng tin trong</span>
                  <InputNumber min={1} max={30} value={days} onChange={(v) => setDays(Number(v) || 1)} addonAfter="ngày" />
                  <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
                    Gửi thông báo đẩy cho mọi người
                  </Checkbox>
                </>
              )}
            </Space>
          )}
        </div>
      )}
    </Modal>
  );
};

/** Hộp đăng bài chỉ dựng khi mở → mỗi lần mở có trạng thái và clientRef mới. */
const ComposerModal = ({ open, onClose }: { open: boolean; onClose: () => void }) =>
  open ? <ComposerBody onClose={onClose} /> : null;

export default ComposerModal;
